import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { stallBucket } from '@/lib/stall';
import {
  deals as mockDeals,
  activities as mockActivities,
  customerFeedback as mockFeedback,
  floorReps as mockFloorReps,
} from '@/data/mockData';
import type {
  Deal,
  Activity,
  CustomerFeedback,
  FloorRep,
  DealStage,
  DealStatus,
  DealPriority,
  NextStep,
  ActivityType,
  FeedbackColor,
} from '@/types';

type DealRow = {
  id: string;
  dealership_id: string;
  rep_id: string | null;
  customer_name: string;
  phone: string | null;
  email: string | null;
  vehicle: string;
  stage: string;
  estimated_value: number;
  priority: string;
  status: string;
  next_step: string | null;
  notes: string | null;
  created_at: string;
  last_activity_at: string;
  license_image_path?: string | null;
  license_data?: unknown;
};

type ActivityRow = {
  id: string;
  dealership_id: string;
  deal_id: string;
  rep_id: string | null;
  activity_type: string;
  details: string | null;
  customer_name: string | null;
  created_at: string;
};

type FeedbackRow = {
  id: string;
  dealership_id: string;
  deal_id: string;
  rep_id: string | null;
  rating_color: string;
  notes: string | null;
  created_at: string;
};

type ProfileLite = { id: string; full_name: string | null; email: string };

function nameFor(profiles: Map<string, ProfileLite>, id: string | null): string {
  if (!id) return 'Unassigned';
  const p = profiles.get(id);
  return p?.full_name || p?.email || 'Unknown';
}

function mapDeal(row: DealRow, profiles: Map<string, ProfileLite>): Deal {
  const stage = row.stage as DealStage;
  // Real-mode deals carry status='active' in DB; derive the stall bucket from
  // last_activity_at so the manager dashboard surfaces 24h/48h stalls without a cron.
  const derivedStatus = stallBucket(row.last_activity_at, stage);
  return {
    id: row.id,
    dealership_id: row.dealership_id,
    rep_id: row.rep_id ?? '',
    rep_name: nameFor(profiles, row.rep_id),
    customer_name: row.customer_name,
    phone: row.phone ?? '',
    email: row.email ?? '',
    vehicle: row.vehicle,
    stage,
    estimated_value: Number(row.estimated_value) || 0,
    priority: row.priority as DealPriority,
    status: derivedStatus,
    next_step: (row.next_step as NextStep | null) ?? null,
    notes: row.notes ?? '',
    created_at: row.created_at,
    last_activity_at: row.last_activity_at,
    license_image_path: row.license_image_path ?? null,
    license_data: (row.license_data && typeof row.license_data === 'object'
      ? (row.license_data as Record<string, unknown>)
      : null),
  };
}

function mapActivity(row: ActivityRow, profiles: Map<string, ProfileLite>): Activity {
  return {
    id: row.id,
    dealership_id: row.dealership_id,
    deal_id: row.deal_id,
    rep_id: row.rep_id ?? '',
    rep_name: nameFor(profiles, row.rep_id),
    activity_type: row.activity_type as ActivityType,
    details: row.details ?? '',
    customer_name: row.customer_name ?? undefined,
    created_at: row.created_at,
  };
}

function mapFeedback(row: FeedbackRow): CustomerFeedback {
  return {
    id: row.id,
    dealership_id: row.dealership_id,
    deal_id: row.deal_id,
    rep_id: row.rep_id ?? '',
    rating_color: row.rating_color as FeedbackColor,
    notes: row.notes ?? '',
    created_at: row.created_at,
  };
}

function buildFloorReps(
  deals: Deal[],
  activities: Activity[],
  rosterProfiles: ProfileLite[],
): FloorRep[] {
  const ids = new Set<string>();
  rosterProfiles.forEach(p => ids.add(p.id));
  deals.forEach(d => d.rep_id && ids.add(d.rep_id));
  const profileMap = new Map(rosterProfiles.map(p => [p.id, p]));

  return Array.from(ids).map(id => {
    const repDeals = deals.filter(d => d.rep_id === id);
    const repActs = activities.filter(a => a.rep_id === id);
    const last = repActs[0]?.created_at ?? repDeals[0]?.last_activity_at ?? new Date(0).toISOString();
    const status: DealStatus = repDeals.some(d => d.status === 'no_activity')
      ? 'no_activity'
      : repDeals.some(d => d.status === 'slowing')
      ? 'slowing'
      : 'active';
    const prof = profileMap.get(id);
    return {
      id,
      name: prof?.full_name || prof?.email || repDeals[0]?.rep_name || 'Rep',
      active_deals: repDeals.filter(d => d.stage !== 'sold').length,
      demos: repActs.filter(a =>
        ['walkaround', 'test_drive', 'quote_review', 'followup_video'].includes(a.activity_type),
      ).length,
      last_activity: last,
      hot_deals: repDeals.filter(d => d.priority === 'hot' && d.stage !== 'sold').length,
      status,
    };
  });
}

export interface DealershipData {
  deals: Deal[];
  activities: Activity[];
  customerFeedback: CustomerFeedback[];
  floorReps: FloorRep[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useDealershipData(): DealershipData {
  const { mode, dealershipId } = useAuth();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [feedback, setFeedback] = useState<CustomerFeedback[]>([]);
  const [floorReps, setFloorReps] = useState<FloorRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (mode === 'demo') {
      setDeals(mockDeals);
      setActivities(mockActivities);
      setFeedback(mockFeedback);
      setFloorReps(mockFloorReps);
      setLoading(false);
      setError(null);
      return;
    }

    if (mode !== 'real' || !dealershipId) {
      setDeals([]);
      setActivities([]);
      setFeedback([]);
      setFloorReps([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const [{ data: dealRows, error: e1 }, { data: actRows, error: e2 }, { data: fbRows, error: e3 }] =
      await Promise.all([
        supabase
          .from('deals')
          .select('*')
          .eq('dealership_id', dealershipId)
          .order('last_activity_at', { ascending: false }),
        supabase
          .from('activities')
          .select('*')
          .eq('dealership_id', dealershipId)
          .order('created_at', { ascending: false })
          .limit(50),
        supabase
          .from('customer_feedback')
          .select('*')
          .eq('dealership_id', dealershipId)
          .order('created_at', { ascending: false }),
      ]);

    if (e1 || e2 || e3) {
      setError(e1?.message ?? e2?.message ?? e3?.message ?? 'Failed to load data');
      setLoading(false);
      return;
    }

    // Resolve rep names via profiles + pull dealership-wide rep roster
    const profiles = new Map<string, ProfileLite>();
    const rosterIdsSet = new Set<string>();
    (dealRows ?? []).forEach((r: DealRow) => r.rep_id && rosterIdsSet.add(r.rep_id));
    (actRows ?? []).forEach((r: ActivityRow) => r.rep_id && rosterIdsSet.add(r.rep_id));

    // Pull every rep on the dealership so an empty floor still lists the team.
    const { data: roleRows } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('dealership_id', dealershipId)
      .eq('role', 'rep');
    const rosterIds = new Set<string>(rosterIdsSet);
    (roleRows ?? []).forEach((r: any) => r.user_id && rosterIds.add(r.user_id));

    if (rosterIds.size > 0) {
      const { data: profRows } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', Array.from(rosterIds));
      (profRows ?? []).forEach((p: any) => profiles.set(p.id, p));
    }

    const repProfileIds = (roleRows ?? []).map((r: any) => r.user_id).filter(Boolean);
    const rosterProfiles: ProfileLite[] = repProfileIds
      .map((id: string) => profiles.get(id))
      .filter(Boolean) as ProfileLite[];

    const mappedDeals = (dealRows ?? []).map((r: DealRow) => mapDeal(r, profiles));
    const mappedActs = (actRows ?? []).map((r: ActivityRow) => mapActivity(r, profiles));
    const mappedFb = (fbRows ?? []).map(mapFeedback);

    setDeals(mappedDeals);
    setActivities(mappedActs);
    setFeedback(mappedFb);
    setFloorReps(buildFloorReps(mappedDeals, mappedActs, rosterProfiles));
    setLoading(false);
  }, [mode, dealershipId]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime: refresh on any change to deals / activities / customer_feedback for this dealership
  useEffect(() => {
    if (mode !== 'real' || !dealershipId) return;
    let pending = false;
    const scheduleRefresh = () => {
      if (pending) return;
      pending = true;
      // Debounce bursts (e.g. template fires N activity rows at once)
      setTimeout(() => { pending = false; load(); }, 250);
    };
    const filter = `dealership_id=eq.${dealershipId}`;
    const channel = supabase
      .channel(`dealership-data:${dealershipId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deals', filter }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'activities', filter }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'customer_feedback', filter }, scheduleRefresh)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [mode, dealershipId, load]);

  return { deals, activities, customerFeedback: feedback, floorReps, loading, error, refresh: load };
}